// ===============================================
// File: src/components/Staff/StaffPage.jsx
// Upgrades:
// - Add form now supports email + color + active
// - Table shows Email, Active, Color swatch
// - Inline Active toggle
// - Quick filters (All/Active/Inactive) + search by name/email/role
// - Keeps updateStaff(id, patch) OR updateStaff({ id, ...patch }) compatibility
// - Currency shown in AUD to match Mudo
// ===============================================

import React, { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useStaff } from "../../contexts/StaffContext";

const roles = ["Owner", "Manager", "Technician", "Maker", "Member"];

const toCurrency = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "A$0.00";
  try {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 2,
    }).format(n);
  } catch {
    return `A$${n.toFixed(2)}`;
  }
};

// small helper so we can call either signature
function applyUpdate(updateStaff, id, patch) {
  try {
    if (typeof updateStaff !== "function") return;
    if (updateStaff.length >= 2) updateStaff(id, patch);
    else updateStaff({ id, ...patch });
  } catch (e) {
    console.warn("[StaffPage] updateStaff error:", e);
  }
}

export default function StaffPage() {
  const navigate = useNavigate();
  const { staffList = [], addStaff, deleteStaff, updateStaff } = useStaff();

  // ---- Add form state ----
  const [name, setName] = useState("");
  const [roleAdd, setRoleAdd] = useState("Member");
  const [rateAdd, setRateAdd] = useState("");
  const [hoursAdd, setHoursAdd] = useState("");
  const [emailAdd, setEmailAdd] = useState("");
  const [colorAdd, setColorAdd] = useState("#7dd3fc"); // light-sky default
  const [activeAdd, setActiveAdd] = useState(true);

  const canAdd = name.trim().length > 0;

  const resetAddForm = () => {
    setName("");
    setRoleAdd("Member");
    setRateAdd("");
    setHoursAdd("");
    setEmailAdd("");
    setColorAdd("#7dd3fc");
    setActiveAdd(true);
  };

  const handleAdd = useCallback(() => {
    if (!canAdd) return;
    const hourlyRate = Number(rateAdd);
    const hoursPerWeek = Number(hoursAdd);
    addStaff({
      name: name.trim(),
      role: roleAdd || "Member",
      hourlyRate: Number.isFinite(hourlyRate) ? hourlyRate : 0,
      hoursPerWeek: Number.isFinite(hoursPerWeek) ? hoursPerWeek : 0,
      email: emailAdd.trim(),
      color: colorAdd,
      isActive: !!activeAdd,
    });
    resetAddForm();
  }, [canAdd, name, roleAdd, rateAdd, hoursAdd, emailAdd, colorAdd, activeAdd, addStaff]);

  const handleDelete = (id) => {
    if (!id) return;
    if (!window.confirm("Remove this staff member?")) return;
    deleteStaff?.(id);
  };

  // ---- Edit state per row ----
  const [editId, setEditId] = useState(null);
  const [editRole, setEditRole] = useState("Member");
  const [editRate, setEditRate] = useState("");
  const [editHours, setEditHours] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editColor, setEditColor] = useState("#7dd3fc");

  const startEdit = (row) => {
    setEditId(row.id);
    setEditRole(row.role || "Member");
    setEditRate(String(Number(row.hourlyRate ?? 0)));
    setEditHours(String(Number(row.hoursPerWeek ?? 0)));
    setEditEmail(String(row.email || ""));
    setEditColor(String(row.color || "#7dd3fc"));
  };
  const cancelEdit = () => {
    setEditId(null);
    setEditRole("Member");
    setEditRate("");
    setEditHours("");
    setEditEmail("");
    setEditColor("#7dd3fc");
  };

  const saveEdit = () => {
    if (!editId) return;
    const hourlyRate = Number(editRate);
    const hoursPerWeek = Number(editHours);
    const patch = {
      role: editRole || "Member",
      hourlyRate: Number.isFinite(hourlyRate) ? hourlyRate : 0,
      hoursPerWeek: Number.isFinite(hoursPerWeek) ? hoursPerWeek : 0,
      email: editEmail.trim(),
      color: editColor,
    };
    applyUpdate(updateStaff, editId, patch);
    cancelEdit();
  };

  // ---- Filters / Search ----
  const [filter, setFilter] = useState("All"); // All | Active | Inactive
  const [q, setQ] = useState("");

  const rows = useMemo(() => {
    const list = Array.isArray(staffList) ? staffList : [];
    const filtered = list.filter((s) => {
      const byActive =
        filter === "All" ? true : filter === "Active" ? s.isActive !== false : s.isActive === false;
      const txt = `${s.name || ""} ${s.email || ""} ${s.role || ""}`.toLowerCase();
      const hit = !q.trim() || txt.includes(q.trim().toLowerCase());
      return byActive && hit;
    });
    return [...filtered].sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""))
    );
  }, [staffList, filter, q]);

  return (
    <div className="p-6 text-white min-h-screen" style={{ background: "var(--color-app-bg, #0f0f10)" }}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <h1 className="text-xl font-bold">Staff Management</h1>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded px-2 py-1">
            <button
              onClick={() => setFilter("All")}
              className={`text-xs px-2 py-1 rounded ${filter === "All" ? "bg-white/20" : ""}`}
            >
              All
            </button>
            <button
              onClick={() => setFilter("Active")}
              className={`text-xs px-2 py-1 rounded ${filter === "Active" ? "bg-white/20" : ""}`}
            >
              Active
            </button>
            <button
              onClick={() => setFilter("Inactive")}
              className={`text-xs px-2 py-1 rounded ${filter === "Inactive" ? "bg-white/20" : ""}`}
            >
              Inactive
            </button>
          </div>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, role…"
            className="px-3 py-1.5 rounded bg-zinc-800 border border-white/10 outline-none w-56"
          />
          <button onClick={() => navigate(-1)} className="px-3 py-1.5 rounded bg-orange-600 hover:bg-orange-700 text-sm">
            ← Back
          </button>
        </div>
      </div>

      {/* Add Staff */}
      <section className="mb-4 rounded border border-white/10 bg-white/5">
        <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2">
          <span className="inline-flex w-2 h-2 bg-orange-400 rounded-sm" />
          <span className="font-semibold">Add Staff</span>
        </div>

        <div className="p-3 grid grid-cols-1 md:grid-cols-[1fr,160px,140px,140px,220px,120px,120px] gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name"
            className="px-3 py-2 rounded bg-zinc-800 border border-white/10 outline-none"
          />
          <select
            value={roleAdd}
            onChange={(e) => setRoleAdd(e.target.value)}
            className="px-3 py-2 rounded bg-zinc-800 border border-white/10 outline-none"
          >
            {roles.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <input
            type="number"
            step="0.01"
            min="0"
            value={rateAdd}
            onChange={(e) => setRateAdd(e.target.value)}
            placeholder="Hourly Rate (AUD)"
            className="px-3 py-2 rounded bg-zinc-800 border border-white/10 outline-none"
          />
          <input
            type="number"
            step="0.1"
            min="0"
            value={hoursAdd}
            onChange={(e) => setHoursAdd(e.target.value)}
            placeholder="Hours/week"
            className="px-3 py-2 rounded bg-zinc-800 border border-white/10 outline-none"
          />
          <input
            type="email"
            value={emailAdd}
            onChange={(e) => setEmailAdd(e.target.value)}
            placeholder="Email (optional)"
            className="px-3 py-2 rounded bg-zinc-800 border border-white/10 outline-none"
          />
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={colorAdd}
              onChange={(e) => setColorAdd(e.target.value)}
              className="h-10 w-full rounded bg-zinc-800 border border-white/10 p-1"
              title="Color tag"
            />
          </div>
          <div className="flex items-center justify-between md:justify-start gap-2">
            <label className="text-xs opacity-80">Active</label>
            <input
              type="checkbox"
              checked={activeAdd}
              onChange={(e) => setActiveAdd(e.target.checked)}
              className="h-5 w-5 accent-emerald-500"
            />
          </div>

          <div className="md:col-start-7 flex items-center md:justify-end">
            <button
              onClick={handleAdd}
              disabled={!canAdd}
              className={`px-3 py-2 rounded text-sm ${
                canAdd ? "bg-emerald-600 hover:bg-emerald-700" : "bg-emerald-900/60 cursor-not-allowed"
              }`}
            >
              + Add
            </button>
          </div>
        </div>
      </section>

      {/* Staff List */}
      <section className="rounded border border-white/10 bg-white/5">
        <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2">
          <span className="inline-flex w-2 h-2 bg-orange-400 rounded-sm" />
          <span className="font-semibold">Staff List</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-white/5 border-b border-white/10">
              <tr className="text-zinc-300">
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Hourly Rate</th>
                <th className="px-3 py-2">Hours/week</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Active</th>
                <th className="px-3 py-2">Color</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? (
                rows.map((s) => {
                  const isEditing = editId === s.id;
                  return (
                    <tr key={s.id} className="border-b border-white/5">
                      <td className="px-3 py-2">{s.name || "—"}</td>

                      <td className="px-3 py-2">
                        {isEditing ? (
                          <select
                            value={editRole}
                            onChange={(e) => setEditRole(e.target.value)}
                            className="px-2 py-1 rounded bg-zinc-800 border border-white/10 outline-none"
                          >
                            {roles.map((r) => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                          </select>
                        ) : (
                          s.role || "—"
                        )}
                      </td>

                      <td className="px-3 py-2">
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editRate}
                            onChange={(e) => setEditRate(e.target.value)}
                            className="w-28 px-2 py-1 rounded bg-zinc-800 border border-white/10 outline-none"
                          />
                        ) : (
                          toCurrency(s.hourlyRate)
                        )}
                      </td>

                      <td className="px-3 py-2">
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            value={editHours}
                            onChange={(e) => setEditHours(e.target.value)}
                            className="w-24 px-2 py-1 rounded bg-zinc-800 border border-white/10 outline-none"
                          />
                        ) : (
                          Number(s.hoursPerWeek ?? 0)
                        )}
                      </td>

                      <td className="px-3 py-2">
                        {isEditing ? (
                          <input
                            type="email"
                            value={editEmail}
                            onChange={(e) => setEditEmail(e.target.value)}
                            className="w-52 px-2 py-1 rounded bg-zinc-800 border border-white/10 outline-none"
                          />
                        ) : (
                          s.email || "—"
                        )}
                      </td>

                      <td className="px-3 py-2">
                        <label className="inline-flex items-center gap-2 select-none">
                          <input
                            type="checkbox"
                            checked={s.isActive !== false}
                            onChange={(e) =>
                              applyUpdate(updateStaff, s.id, { isActive: e.target.checked })
                            }
                            className="h-5 w-5 accent-emerald-500"
                          />
                          <span className="text-xs opacity-80">{s.isActive !== false ? "Active" : "Inactive"}</span>
                        </label>
                      </td>

                      <td className="px-3 py-2">
                        {isEditing ? (
                          <input
                            type="color"
                            value={editColor}
                            onChange={(e) => setEditColor(e.target.value)}
                            className="h-8 w-14 rounded bg-zinc-800 border border-white/10 p-1"
                            title="Color tag"
                          />
                        ) : (
                          <span className="inline-flex items-center gap-2">
                            <span
                              className="inline-block w-4 h-4 rounded-full border border-white/20"
                              style={{ background: s.color || "#fff" }}
                              title={s.color || "#fff"}
                            />
                            <span className="text-xs opacity-70">{s.color || "—"}</span>
                          </span>
                        )}
                      </td>

                      <td className="px-3 py-2">
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={saveEdit}
                              className="px-2.5 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-xs"
                            >
                              Save
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="px-2.5 py-1 rounded bg-zinc-600 hover:bg-zinc-700 text-xs"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => startEdit(s)}
                              className="px-2.5 py-1 rounded bg-blue-600 hover:bg-blue-700 text-xs"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(s.id)}
                              className="px-2.5 py-1 rounded bg-red-600 hover:bg-red-700 text-xs"
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className="px-3 py-3 text-zinc-400" colSpan={8}>
                    No staff found — add someone above or adjust filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
